/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * datetime-local yerine date + time; kullanıcı timeFormat (12h/24h) tercihine uyar.
 * Değer sözleşmesi: `YYYY-MM-DDTHH:mm` (datetime-local ile uyumlu).
 */
import React, { useMemo } from 'react';
import { useDateTime } from '../../hooks/useDateTime';
import './DateTimeLocalInput.css';

export interface DateTimeLocalInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  required?: boolean;
  'aria-label'?: string;
}

export function splitDateTimeLocal(value: string): { date: string; time: string } {
  const s = String(value ?? '').trim();
  if (!s) return { date: '', time: '' };
  if (!s.includes('T')) {
    return { date: /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '', time: '' };
  }
  const [date, timeRaw] = s.split('T');
  const time = (timeRaw ?? '').slice(0, 5);
  return { date: date ?? '', time: /^\d{2}:\d{2}$/.test(time) ? time : '' };
}

export function joinDateTimeLocal(date: string, time: string): string {
  const d = String(date ?? '').trim();
  const t = String(time ?? '').trim();
  if (!d) return '';
  if (!t) return `${d}T00:00`;
  return `${d}T${t.slice(0, 5)}`;
}

/** timeFormat → time input locale (tarayıcı 12/24 saat gösterimi). */
export function resolveTimeInputLang(timeFormat: '12h' | '24h'): string {
  return timeFormat === '12h' ? 'en-US' : 'tr-TR';
}

export const DateTimeLocalInput: React.FC<DateTimeLocalInputProps> = ({
  value,
  onChange,
  disabled,
  className,
  id,
  required,
  'aria-label': ariaLabel,
}) => {
  const { timeFormat } = useDateTime();
  const { date, time } = useMemo(() => splitDateTimeLocal(value), [value]);
  const timeLang = resolveTimeInputLang(timeFormat);
  const errorClass = className?.includes('input-error') ? ' input-error' : '';

  const patch = (nextDate: string, nextTime: string) => {
    onChange(joinDateTimeLocal(nextDate, nextTime));
  };

  return (
    <div className={`datetime-local-input${errorClass}`} id={id}>
      <input
        type="date"
        value={date}
        onChange={(e) => patch(e.target.value, time)}
        disabled={disabled}
        required={required && !date}
        className={`form-input datetime-local-input__date${errorClass}`}
        aria-label={ariaLabel ? `${ariaLabel} — date` : undefined}
      />
      <input
        type="time"
        key={`time-${timeLang}`}
        lang={timeLang}
        step={60}
        value={time}
        onChange={(e) => patch(date, e.target.value)}
        disabled={disabled}
        required={required && !!date && !time}
        className={`form-input datetime-local-input__time${errorClass}`}
        aria-label={ariaLabel ? `${ariaLabel} — time` : undefined}
      />
    </div>
  );
};

export default DateTimeLocalInput;
